import { useEffect, useState, useRef, useMemo } from "react";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Tooltip,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";

import { Circle as CircleIcon } from "@mui/icons-material";
import LoadingScreen from "components/Loading";

import { useAuth } from "context/authContext";
import { getCompetenciesForManager } from "services/api";
import { getCompetenciesToJobsWithGroupName } from "services/api";
import EmployeeAvatar from "components/EmployeeAvatar";
import {
  getCompetencyLevelsForJobOnDate,
  addCompetencyToEmployee,
  saveCompToEmployeeEvidenceData,
  downloadCompToEmployeeEvidenceData,
  updateCompToEmployeeEvidenceComment,
  deleteCompToEmployeeEvidence,
} from "services/api";
import { updateCompetencyLevelForEmp } from "services/api";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import SnackbarWrapper from "components/Snackbar";
import IconButton from "@mui/material/IconButton";
import ConfirmationDialog from "components/ConfirmationDialog";
import EmployeePointsDialog from "./components/EmployeePointsDialog";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { Download, Clear } from "@mui/icons-material";
import EvidenceCommentButton from "components/EvidenceCommentButton";
import EmployeeCompetencyCard from "components/EmployeeCompetencyCard";

const CompetencyToEmpTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { employee, selectedMonth, setSelectedMonth, selectedYear, setSelectedYear } = useAuth();
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [employeeLevelStatus, setEmployeeLevelStatus] = useState({});
  const snackbarRef = useRef();
  const [jobLevels, setJobLevels] = useState({});
  const [selectedJobId, setSelectedJobId] = useState("all");
  const [view, setView] = useState("byJob");
  const [selectedCompId, setSelectedCompId] = useState("all");
  const [expandedStates, setExpandedStates] = useState({});
  const [evidences, setEvidences] = useState({});
  const [evidenceComments, setEvidenceComments] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [confOpen, setConfOpen] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [selectedEmployeeForPoints, setSelectedEmployeeForPoints] = useState(null);
  const [compToJobMappings, setCompToJobMappings] = useState({}); // Store bwActive status
  const [employeePointsSummary, setEmployeePointsSummary] = useState({});

  const toggleExpand = (compId) => {
    setExpandedStates((prev) => ({
      ...prev,
      [compId]: !prev[compId],
    }));
  };

  const handleLevelSelect = async (employeeId, selectedValue, compMeta) => {
    const [
      compId, // 0
      compName, // 1
      def, // 2
      type, // 3
      targetLvlId, // 4
      targetLvl, // 5
      targetLvlValue, // 6 // 7 // 8 // 9 // 10 // 11 // 12 // 13
      ,
      ,
      ,
      ,
      ,
      ,
      ,
      empName, // 14
      jobId,
      jobName,
      photo, // 16 ✅ include jobName
    ] = compMeta;

    const parsedJobId = parseInt(jobId, 10);
    const parsedCompId = parseInt(compId, 10);

    const levelsForJob = jobLevels[parsedJobId] || [];
    const selectedLevel = levelsForJob.find(
      (lvl) => lvl.name === selectedValue && lvl.competencyId === parsedCompId
    );

    const compLevlId = selectedLevel?.id;

    const recordExists = employeeLevelStatus[employeeId]?.[compId];

    if (recordExists) {
      const competenciesUnderEmp = employeeDetails[employeeId];
      const targetCompetency = competenciesUnderEmp.find((comp) => comp[0] === compId);

      if (targetCompetency) {
        const recordMonth = targetCompetency[10];
        const recordYear = targetCompetency[11];
        const recordId = targetCompetency[12];

        if (recordMonth === selectedMonth && recordYear === selectedYear) {
          if (recordId && compLevlId) {
            const updated = await updateCompetencyLevelForEmp(recordId, compLevlId);
            if (updated !== "error") {
              // Delete old evidence when level changes
              await deleteCompToEmployeeEvidence(recordId);

              // Clear local evidence states
              setEvidences((prev) => ({
                ...prev,
                [recordId]: null,
              }));

              setEvidenceComments((prev) => ({
                ...prev,
                [recordId]: "",
              }));

              setEmployeeDetails((prev) => {
                const existing = prev[employeeId] || [];
                const updatedRecord = [...targetCompetency];
                updatedRecord[8] = selectedValue;
                updatedRecord[13] = selectedLevel?.value ?? null;

                const index = existing.findIndex((item) => item[0] === compId);
                if (index >= 0) {
                  const updatedList = [...existing];
                  updatedList[index] = updatedRecord;
                  return { ...prev, [employeeId]: updatedList };
                }
                return prev;
              });

              snackbarRef.current?.showSnackbar(t("user.competencies.compLvlUpdated"), "success");
            } else {
              snackbarRef.current?.showSnackbar(
                t("user.competencies.compLvlUpdateFailed"),
                "error"
              );
            }
          }
        } else {
          await tryAddCompetencyToEmployee();
        }
      }
    } else {
      await tryAddCompetencyToEmployee();
    }

    async function tryAddCompetencyToEmployee() {
      if (compLevlId) {
        const success = await addCompetencyToEmployee({
          month: selectedMonth,
          year: selectedYear,
          actionTaken: false,
          compLevelId: compLevlId,
          targetCompLevelId: targetLvlId,
          employeeId,
          compId,
          trainingId: null,
        });

        const recordId = success?.id;
        if (recordId) {
          setEmployeeDetails((prev) => {
            const existing = prev[employeeId] || [];
            const newRecord = [
              compId,
              compName,
              def,
              type,
              targetLvlId,
              targetLvl,
              targetLvlValue,
              employeeId,
              selectedValue,
              null,
              selectedMonth,
              selectedYear,
              recordId,
              selectedLevel?.value ?? null,
              empName,
              jobId,
              jobName,
              photo ?? "",
            ];

            const index = existing.findIndex((item) => item[0] === compId);
            const updated = [...existing];
            if (index >= 0) {
              updated[index] = newRecord;
            } else {
              updated.push(newRecord);
            }
            return { ...prev, [employeeId]: updated };
          });

          snackbarRef.current?.showSnackbar(t("user.competencies.compLvlAdded"), "success");

          setEmployeeLevelStatus((prev) => ({
            ...prev,
            [employeeId]: {
              ...(prev[employeeId] || {}),
              [compId]: true,
            },
          }));
        } else {
          snackbarRef.current?.showSnackbar(t("user.competencies.compLvlAddFailed"), "error");
        }
      }
    }
  };

  const handleDownloadCompToEmployeeEvidence = async (compToEmpId, fileName) => {
    if (!fileName || !compToEmpId) return;

    setDownloadLoading(true);

    try {
      await downloadCompToEmployeeEvidenceData(compToEmpId, fileName);
    } catch (err) {
      console.error("Error downloading competency evidence:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDeleteCompToEmployeeEvidence = async (compToEmpId) => {
    setLoading(true);

    const result = await deleteCompToEmployeeEvidence(compToEmpId);

    if (result !== "error") {
      setEvidences((prev) => ({
        ...prev,
        [compToEmpId]: null,
      }));

      setEvidenceComments((prev) => ({
        ...prev,
        [compToEmpId]: "",
      }));

      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.evidenceDelete"), "success");
    } else {
      snackbarRef.current?.showSnackbar(t("user.evidenceProvider.evidenceDeleteFail"), "error");
    }

    setLoading(false);
  };

  useEffect(() => {
    setEmployeeLevelStatus({}); // 3ando level aw laa fel comp dee
    setEmployeeDetails({});
  }, [selectedMonth, selectedYear]);
  // Resets all the relevant maps whenever the selected month or year changes

  const fetchAllCompetencyData = async () => {
    // setDetailsLoading(true);
    setLoading(true);
    try {
      const data = await getCompetenciesForManager(employee.id, selectedYear, selectedMonth);
      console.log("Data::", data);

      if (data !== "error") {
        const empDetailsMap = {};
        const empLevelStatusMap = {};
        const jobLevelsMap = {};
        const uniqueJobIds = new Set();

        // data for each record
        data.forEach((row) => {
          const [
            compId,
            compName,
            compDef,
            compType,
            compLevelId, //lvl required by job
            compLevelName,
            compLevelValue,
            empId,
            empLevelName,
            trainingName,
            recordMonth,
            recordYear,
            recordId,
            empLevelValue,
            empName,
            jobId,
            jobName,
            photo,
            // NEW
            evidenceId,
            evidenceFileName,
            evidenceComment,
            adminComments,
          ] = row;

          uniqueJobIds.add(jobId);

          // 1. Set employee detail
          if (!empDetailsMap[empId]) empDetailsMap[empId] = [];
          empDetailsMap[empId].push(row);
          // 2. Set level status
          if (!empLevelStatusMap[empId]) empLevelStatusMap[empId] = {};
          if (empLevelValue !== null && empLevelValue !== undefined) {
            empLevelStatusMap[empId][compId] = true;
          }
          // 3. Store available levels per employee
        });
        for (const jobId of uniqueJobIds) {
          console.log("Job Id::",jobId);
          const levels = await getCompetencyLevelsForJobOnDate(jobId, selectedMonth, selectedYear);
          console.log("Comp Levels", levels);
          const formattedLevels = levels.map((level) => ({
            id: level[0],
            name: level[1],
            value: level[2],
            competencyId: level[3],
          }));
          jobLevelsMap[jobId] = formattedLevels;
        }

        setEmployeeDetails(empDetailsMap); //employee details (comp + emp's info in comp)
        setEmployeeLevelStatus(empLevelStatusMap); //emp 3ando level aw laa fe kol comp
        setJobLevels(jobLevelsMap); //levels for job
      } else {
        setEmployeeDetails({});
        setEmployeeLevelStatus({});
        setJobLevels({});
      }
    } catch (e) {
      setEmployeeDetails({});
      setEmployeeLevelStatus({});
    } finally {
      // setDetailsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCompetencyData();
  }, [employee.id, selectedMonth, selectedYear]);

  // Add this useEffect to fetch comp-to-job mappings with bwActive status
  useEffect(() => {
    const fetchMappings = async () => {
      const mappings = await getCompetenciesToJobsWithGroupName();
      if (mappings !== "error") {
        const mappingMap = {};
        mappings.forEach((m) => {
          // Create a key from jobId and compLevelId (or competencyId)
          // You might need to adjust this based on your data structure
          const jobId = m.jobId;
          const compLevelId = m.compLevelId;
          mappingMap[`${jobId}_${compLevelId}`] = {
            compToJobId: m.id,
            bwActive: m.bwActive,
          };
        });
        setCompToJobMappings(mappingMap);
      }
    };

    fetchMappings();
  }, []);

  const getBwStatus = (jobId, compMeta) => {
    const compLevelId = compMeta[4]; // Based on your data structure
    const key = `${jobId}_${compLevelId}`;
    return compToJobMappings[key] || null;
  };

  // Add this function to fetch points summary for an employee
  const fetchEmployeePointsSummary = async (empId, jobId) => {
    // This would need to be called for each employee/job combination
    // Implementation depends on your API structure
  };

  // Add a refresh function
  const refreshCompetencyData = () => {
    fetchAllCompetencyData();
  };

  const { jobToEmployeeMap, jobOptions, competencyToJobMap, competencyOptions } = useMemo(() => {
    const jobToEmpMap = {};
    const optionsMap = new Map();
    const compToJobMap = {};

    Object.entries(employeeDetails).forEach(([empId, empDetails]) => {
      empDetails.forEach((comp) => {
        const compId = parseInt(comp[0], 10);
        const compMeta = comp;
        const jobId = parseInt(comp[15], 10);
        const jobName = comp[16];
        const photo = comp[17];
        const empName = comp[14];

        // ----------- JOB TO EMPLOYEE MAPPING -------------
        if (!jobToEmpMap[jobId]) {
          jobToEmpMap[jobId] = {
            jobName,
            employees: {},
          };
        }

        if (!jobToEmpMap[jobId].employees[empId]) {
          jobToEmpMap[jobId].employees[empId] = {
            empName,
            photo,
            competencies: {},
          };
          if (!optionsMap.has(jobId)) {
            optionsMap.set(jobId, jobName);
          }
        }

        jobToEmpMap[jobId].employees[empId].competencies[compId] = {
          compMeta,
          currentLevel: {
            name: comp[8],
            value: comp[13],
          },
          availableLevels: (jobLevels[jobId] || []).filter((lvl) => lvl.competencyId === compId),
        };

        // ----------- COMPETENCY TO JOB MAPPING -------------
        if (!compToJobMap[compId]) {
          compToJobMap[compId] = {
            compMeta,
            jobs: {},
          };
        }

        if (!compToJobMap[compId].jobs[jobId]) {
          compToJobMap[compId].jobs[jobId] = {
            jobName,
            employees: {},
          };
        }

        if (!compToJobMap[compId].jobs[jobId].employees[empId]) {
          compToJobMap[compId].jobs[jobId].employees[empId] = {
            empName,
            photo,
            currentLevel: {
              name: comp[8],
              value: comp[13],
            },
            availableLevels: (jobLevels[jobId] || []).filter((lvl) => lvl.competencyId === compId),
            compMeta: comp,
          };
        }
      });
    });

    const jobOptions = [
      { id: "all", name: t("user.dashboards.allJobs") },
      ...Array.from(optionsMap.entries()).map(([id, name]) => ({
        id: id.toString(),
        name,
      })),
    ];

    const competencyOptions = [
      { id: "all", name: t("user.dashboards.allComps") },
      ...Object.entries(compToJobMap).map(([compId, compData]) => ({
        id: compId,
        name: compData?.compMeta?.[1] || "Unnamed",
      })),
    ];

    return {
      jobToEmployeeMap: jobToEmpMap,
      jobOptions,
      competencyToJobMap: compToJobMap,
      competencyOptions,
    };
  }, [employeeDetails, jobLevels]);

  const handleSaveCompToEmployeeEvidence = async (compToEmpId, file) => {
    try {
      const res = await saveCompToEmployeeEvidenceData({
        compToEmpId,
        comment: "",
        file,
      });

      if (res !== "error") {
        snackbarRef.current?.showSnackbar(t("user.competencies.saveEvidence"), "success");

        setEvidences((prev) => ({
          ...prev,
          [compToEmpId]: res.fileName || file.name,
        }));
      } else {
        snackbarRef.current?.showSnackbar(t("user.competencies.saveEvidenceFailed"), "error");
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("user.competencies.saveEvidenceFailed"), "error");

      console.error(err);
    }
  };

  const handleSaveCompToEmployeeEvidenceComment = async (compToEmpId, newComment) => {
    try {
      // Call API
      await updateCompToEmployeeEvidenceComment(compToEmpId, newComment);

      // Update local state
      setEvidenceComments((prev) => ({
        ...prev,
        [compToEmpId]: newComment,
      }));

      snackbarRef.current?.showSnackbar(t("user.competencies.commentSaved"), "success");
    } catch (err) {
      console.error("Error saving competency evidence comment:", err);

      snackbarRef.current?.showSnackbar(t("user.competencies.commentError"), "error");
    }
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        {t("user.competencies.assessCompetencies")}
      </Typography>
      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(e, val) => val && setView(val)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="byJob">{t("user.competencies.byJob")}</ToggleButton>
        <ToggleButton value="byCompetency">{t("user.competencies.byComp")}</ToggleButton>
      </ToggleButtonGroup>

      {view === "byJob" && (
        <Box sx={{ mb: 3, width: 250, ...fieldStyles }}>
          <Autocomplete
            size="small"
            value={jobOptions.find((opt) => opt.id === selectedJobId) || jobOptions[0]}
            onChange={(e, newValue) => setSelectedJobId(newValue?.id || "all")}
            options={jobOptions}
            getOptionLabel={(option) => option?.name || "All"}
            renderInput={(params) => (
              <TextField {...params} label={t("user.competencies.filterByJob")} fullWidth />
            )}
          />
        </Box>
      )}

      {view === "byCompetency" && (
        <Box sx={{ mb: 3, width: 250, ...fieldStyles }}>
          <Autocomplete
            size="small"
            value={
              competencyOptions.find((opt) => opt.id === selectedCompId) || competencyOptions[0]
            }
            onChange={(e, newValue) => setSelectedCompId(newValue?.id || "all")}
            options={competencyOptions}
            getOptionLabel={(option) => option?.name || "All"}
            renderInput={(params) => (
              <TextField {...params} label={t("user.competencies.filterByComp")} fullWidth />
            )}
          />
        </Box>
      )}

      {view === "byJob" &&
        Object.entries(jobToEmployeeMap)
          .filter(([jobId]) => selectedJobId === "all" || selectedJobId === jobId)
          .map(([jobId, jobData]) => (
            <Card
              key={jobId}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "12px",
                background: "#ffffff",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                border: "1px solid #f1f5f9",
                mb: 3,
              }}
              variant="outlined"
            >
              {/* Job Title */}
              <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: 16 } }}>
                {jobData.jobName}
              </Typography>

              {Object.entries(jobData.employees).map(([empId, empData]) => (
                <EmployeeCompetencyCard
                  key={empId}
                  jobId={jobId}
                  empId={empId}
                  empData={empData}
                  t={t}
                  levelEditable={true} // Or a state variable
                  evidences={evidences}
                  evidenceComments={evidenceComments}
                  downloadLoading={downloadLoading}
                  getBwStatus={getBwStatus}
                  handleLevelSelect={handleLevelSelect}
                  handleSaveCompToEmployeeEvidence={handleSaveCompToEmployeeEvidence}
                  handleDownloadCompToEmployeeEvidence={handleDownloadCompToEmployeeEvidence}
                  handleSaveCompToEmployeeEvidenceComment={handleSaveCompToEmployeeEvidenceComment}
                  setPendingDelete={setPendingDelete}
                  setConfOpen={setConfOpen}
                  setSelectedEmployeeForPoints={setSelectedEmployeeForPoints}
                  setPointsDialogOpen={setPointsDialogOpen}
                />
              ))}
            </Card>
          ))}

      {view === "byCompetency" &&
        Object.entries(competencyToJobMap)
          .filter(([compId]) => selectedCompId === "all" || selectedCompId === compId)
          .map(([compId, compData]) => {
            const [, compName, definition, type] = compData.compMeta;
            const isExpanded = expandedStates[compId] || false;

            return (
              <Card
                key={compId}
                sx={{
                  mb: 4,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                  border: "1px solid #f1f5f9",
                }}
              >
                {/* Competency Header */}
                <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: 18, sm: 22 } }}>
                  {compName}
                </Typography>

                {/* Definition */}
                <Box sx={{ pl: { xs: 0, sm: 2 }, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t("user.training.definition")}:
                  </Typography>

                  <Card
                    sx={{
                      borderRadius: 0,
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      p: 0,
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        "&:last-child": { paddingBottom: 0 },
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        sx={{
                          fontSize: "0.875rem",
                          lineHeight: 1.4,
                          overflow: isExpanded ? "visible" : "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: isExpanded ? "none" : 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {stripHtmlTags(definition)}
                      </Typography>

                      <Button
                        onClick={() => toggleExpand(compId)}
                        size="small"
                        sx={{
                          mt: 0.5,
                          p: 0,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          minWidth: "auto",
                        }}
                      >
                        {isExpanded ? t("admin.training.showLess") : t("admin.training.showMore")}
                      </Button>
                    </CardContent>
                  </Card>

                  <Typography variant="caption" color="text.secondary">
                    {t("user.training.type")}: {type}
                  </Typography>
                </Box>

                {/* Table Container */}
                <Box
                  sx={{
                    border: "1px solid #e0e2e5",
                    borderRadius: "8px",
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  {/* Header (Hidden on Mobile) */}
                  <Box
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      px: 2,
                      py: 1.5,
                      backgroundColor: "#F6F5FA",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {/* Left column */}
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t("user.training.employee")}
                    </Typography>

                    {/* Right column */}
                    <Typography
                      sx={{
                        flex: "0 0 auto",
                        textAlign: "right",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        mr: 4,
                      }}
                    >
                      {t("user.trainings.currentLvl")}
                    </Typography>
                  </Box>

                  {/* Rows */}
                  {Object.entries(compData.jobs).map(([jobId, jobData]) =>
                    Object.entries(jobData.employees).map(([empId, emp]) => {
                      const bwMapping = getBwStatus(jobId, emp.compMeta);
                      const isBwActive = bwMapping?.bwActive || false;
                      const compToJobId = bwMapping?.compToJobId;

                      return (
                        <Box
                          key={`${jobId}_${empId}`}
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            px: 2,
                            py: 1.5,
                            pl: 3,
                            gap: 1.5,
                            borderBottom: "1px solid #e0e3e8",
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          {/* Left Side - Employee Info */}
                          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  src={
                                    emp.photo ? `data:image/jpeg;base64,${emp.photo}` : undefined
                                  }
                                  sx={{ width: 32, height: 32 }}
                                />
                                <Typography fontWeight={500} sx={{ fontSize: { xs: 14, sm: 16 } }}>
                                  {emp.empName}
                                </Typography>
                              </Box>

                              <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
                                {jobData.jobName}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Middle - BW Points Indicator */}
                          <Box
                            sx={{
                              flex: "0 0 auto",
                              width: { xs: "100%", sm: "80px" },
                              display: "flex",
                              justifyContent: "center",
                              my: { xs: 1, sm: 0 },
                            }}
                          >
                            {isBwActive ? (
                              <Tooltip title="Manage Black/White Points">
                                <Chip
                                  icon={
                                    <Box sx={{ display: "flex", gap: 0.5, mr: 0.5 }}>
                                      <CircleIcon sx={{ fontSize: 8, color: "#757575" }} />
                                      <CircleIcon sx={{ fontSize: 8, color: "#212121" }} />
                                    </Box>
                                  }
                                  label="BW"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  onClick={() => {
                                    setSelectedEmployeeForPoints({
                                      empId,
                                      empName: emp.empName,
                                      compToJobId,
                                      compName,
                                    });
                                    setPointsDialogOpen(true);
                                  }}
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": {
                                      bgcolor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled"></Typography>
                            )}
                          </Box>

                          {/* Right Side - Level Selector */}
                          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            {/* Mobile Label */}
                            <Typography
                              variant="caption"
                              sx={{
                                display: { xs: "block", sm: "none" },
                                mb: 0.5,
                                color: "text.secondary",
                              }}
                            >
                              {t("user.trainings.currentLvl")}
                            </Typography>

                            <TextField
                              size="small"
                              select
                              fullWidth
                              value={emp.currentLevel?.name ?? ""}
                              onChange={(e) =>
                                handleLevelSelect(empId, e.target.value, emp.compMeta)
                              }
                              disabled={isBwActive}
                              helperText={
                                isBwActive ? "Level is auto-calculated from BW points" : ""
                              }
                              sx={{
                                width: { xs: "100%", sm: 160 },
                                "& .MuiInputBase-root": {
                                  height: 36,
                                  borderRadius: "8px",
                                  background: isBwActive ? "#f0f7ff" : "#fff",
                                  border: isBwActive ? "1px solid #90caf9" : "none",
                                },
                              }}
                            >
                              {emp.availableLevels.map((lvl) => (
                                <MenuItem key={lvl.id} value={lvl.name}>
                                  {lvl.name} ({lvl.value})
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  {/* Empty State */}
                  {Object.entries(compData.jobs).length === 0 && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        No employees found for this competency
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            );
          })}
      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message="Are you sure you want to delete Evidence?"
        onConfirm={() => {
          if (pendingDelete) {
            handleDeleteCompToEmployeeEvidence(pendingDelete.compToEmpId);
            setPendingDelete(null);
          }

          setConfOpen(false);
        }}
      />

      <EmployeePointsDialog
        open={pointsDialogOpen}
        onClose={() => {
          setPointsDialogOpen(false);
          setSelectedEmployeeForPoints(null);
        }}
        employeeId={selectedEmployeeForPoints?.empId}
        compToJobId={selectedEmployeeForPoints?.compToJobId}
        employeeName={selectedEmployeeForPoints?.empName}
        competencyName={selectedEmployeeForPoints?.compName}
        snackbarRef={snackbarRef}
        onPointsChanged={refreshCompetencyData}
        canChangePoints={true}
      />

      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </Box>
  );
};

export default CompetencyToEmpTab;
